import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { FileAudio, Loader2, Mic2, UploadCloud } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import VoicePicker from "@/components/voicesongs/VoicePicker";
import VoiceSongJobs from "@/components/voicesongs/VoiceSongJobs";
import useJobsPoll from "@/components/voicesongs/useJobsPoll";
import { ACCEPT, MAX_BYTES } from "@/lib/enhanceApi";
import { VOICE_SONG_ACTIVE, listVoiceSongs, uploadVoiceSong } from "@/lib/voiceSongsApi";
import { safeApiError } from "@/lib/safeErrorText";

const OK = /\.(wav|mp3|m4a|aac|flac|aiff?|ogg)$/i;
const isActive = (r) => VOICE_SONG_ACTIVE.includes(r.status);

/** My Voices — upload any song file (with a vocal on it) and hear it sung in your voice; plus every song you've done this way. */
export default function SingUploadSection() {
  const input = useRef(null);
  const [file, setFile] = useState(null);
  const [pick, setPick] = useState({ voice_id: null, pitch: 0 });
  const [ready, setReady] = useState(false);
  const [progress, setProgress] = useState(null);
  const load = useCallback(() => listVoiceSongs(), []);
  const jobs = useJobsPoll(load, isActive, (j) => toast.success(`“${j.result?.title}” is ready`, { description: "Saved to your Music Library." }));
  const choose = (f) => {
    if (!f) return;
    if (!OK.test(f.name) || f.size > MAX_BYTES) { toast.error("Use a WAV, MP3, M4A, AAC, FLAC, AIFF or OGG file under 300 MB."); return; }
    setFile(f);
  };
  const go = async () => {
    setProgress(0);
    try { await uploadVoiceSong(file, { title: file.name.replace(/\.[^.]+$/, ""), ...pick }, setProgress); toast.success("Uploaded — singing it in your voice now"); setFile(null); jobs.refresh(); }
    catch (e) { toast.error("Couldn't start", { description: safeApiError(e) }); }
    finally { setProgress(null); }
  };
  return (
    <section data-testid="sing-upload-section" className="rounded-3xl border border-white/[0.08] bg-[#0a0d1c]/80 p-5 sm:p-6">
      <h2 className="flex items-center gap-2 font-display text-base font-semibold md:text-lg"><Mic2 className="h-4 w-4 text-cyan-300" /> Sing a song in your voice</h2>
      <p className="mt-1 text-[13px] text-white/55">Upload a song made anywhere — the vocals are split off, re-sung in your trained voice and put back with the music. For songs in your library, use “Sing It in My Voice” on the song's page.</p>
      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <button type="button" data-testid="sing-upload-pick" disabled={progress !== null} onClick={() => input.current?.click()}
          className="flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-white/15 bg-black/30 px-4 py-8 text-center hover:border-white/30">
          {file ? <FileAudio className="h-8 w-8 text-cyan-300" /> : <UploadCloud className="h-8 w-8 text-white/50" />}
          <span data-testid="sing-upload-file" className="text-[14px] font-medium">{file ? file.name : "Choose a song file"}</span>
          <span className="text-[11px] text-white/45">WAV, MP3, M4A… up to 15 minutes</span>
        </button>
        <input ref={input} type="file" accept={ACCEPT} className="hidden" data-testid="sing-upload-input" onChange={(e) => choose(e.target.files?.[0])} />
        <div>
          <VoicePicker value={pick} onChange={setPick} onReady={setReady} />
          {progress !== null ? (
            <div className="mt-4"><Progress value={Math.round(progress * 100)} className="h-2 bg-white/10" /><p className="mt-1 text-[12px] text-white/55">Uploading… {Math.round(progress * 100)}%</p></div>
          ) : (
            <button type="button" data-testid="sing-upload-start" onClick={go} disabled={!file || !ready || !pick.voice_id}
              className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-full bg-cyan-300 py-2.5 text-[13px] font-semibold text-black disabled:opacity-40">
              {progress !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mic2 className="h-4 w-4" />} Sing it in my voice
            </button>
          )}
        </div>
      </div>
      <VoiceSongJobs rows={jobs.rows} onChanged={jobs.refresh} testId="sing-upload-jobs" />
    </section>
  );
}
